/**
 * Время в кабинете хранится минутами от полуночи, а показывается как
 * «09:30». Преобразование живёт здесь, а не в формах: их две, и разойтись
 * в трактовке им нельзя.
 */

/** «09:30» → 570. Возвращает null, если строка не время. */
export function timeToMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null

  return hours * 60 + minutes
}

/** 570 → «09:30». */
export function minutesToTime(total: number): string {
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  return `${pad(hours)}:${pad(minutes)}`
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}

/**
 * Названия дней, индекс — как в Date.getDay() и time.Weekday в Go:
 * воскресенье нулевое.
 */
export const WEEKDAY_NAMES = [
  'Воскресенье',
  'Понедельник',
  'Вторник',
  'Среда',
  'Четверг',
  'Пятница',
  'Суббота',
] as const

export const WEEKDAY_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'] as const

/**
 * Порядок показа — с понедельника.
 *
 * Неделя в коде начинается с воскресенья, а в России её так не читают:
 * список, где выходной стоит первым, каждый раз заставляет
 * перепроверять, что выбрал.
 */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const

export function weekdayName(weekday: number): string {
  return WEEKDAY_NAMES[weekday] ?? `День ${weekday}`
}

export function weekdayShort(weekday: number): string {
  return WEEKDAY_SHORT[weekday] ?? '—'
}

export function isWeekend(weekday: number): boolean {
  return weekday === 0 || weekday === 6
}

/** «сегодня в 14:32» — для отметок «последний вход», «сохранено». */
export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—'

  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'

  const today = new Date()
  const sameDay =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()

  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  if (sameDay) return `сегодня в ${time}`

  const day = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
  return `${day}, ${time}`
}

/**
 * Текущее время в часовом поясе компании.
 *
 * Нужно на панели: владелец видит, что у агента «сейчас» не то же самое,
 * что у него, — иначе разница в часах обнаруживается только по звонку
 * в три ночи.
 */
export function nowInTimezone(timezone: string): string {
  try {
    return new Date().toLocaleTimeString('ru-RU', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    // Неизвестный браузеру пояс — не повод ронять страницу.
    return '—'
  }
}
